import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import {
  BusinessDocument,
  BusinessDocumentWorkspaceModel,
} from '../../../core/services/business-document.service';
import {
  DocumentPdfService,
  PdfDocumentPayload,
} from '../../../core/services/document-pdf.service';

@Component({
  selector: 'app-business-document-view',
  standalone: false,
  templateUrl: './business-document-view.html',
  styleUrl: './business-document-view.scss',
  encapsulation: ViewEncapsulation.None,
})
export class BusinessDocumentView implements OnInit, OnDestroy {
  @Input() businessDocumentIdInput = '';
  @Input() embedded = false;
  @Output() closed = new EventEmitter<void>();

  loading = true;
  errorMessage = '';
  workspace: BusinessDocumentWorkspaceModel | null = null;
  printable: PdfDocumentPayload | null = null;

  private businessDocumentId = '';
  private returnUrl = '/home';
  private printTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly documents: BusinessDocument,
    private readonly pdf: DocumentPdfService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.businessDocumentId = this.businessDocumentIdInput.trim()
      || String(this.route.snapshot.paramMap.get('id') || '').trim();
    const requestedReturnUrl = String(this.route.snapshot.queryParamMap.get('returnUrl') || '');
    if (requestedReturnUrl.startsWith('/') && !requestedReturnUrl.startsWith('//')) {
      this.returnUrl = requestedReturnUrl;
    }
    void this.load();
  }

  ngOnDestroy(): void {
    if (this.printTimer) clearTimeout(this.printTimer);
    document.body.classList.remove('business-document-view-printing');
  }

  async load(): Promise<void> {
    if (!this.businessDocumentId) {
      this.loading = false;
      this.errorMessage = 'The business-document identifier is missing.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';
    try {
      this.workspace = await firstValueFrom(this.documents.workspace(this.businessDocumentId));
      this.printable = this.buildPrintable(this.workspace);
      if (this.route.snapshot.queryParamMap.get('print') === '1') {
        this.printTimer = setTimeout(() => this.print(), 0);
      }
    } catch (error: any) {
      this.errorMessage = error?.error?.message ?? 'The business document could not be loaded.';
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  close(): void {
    if (this.embedded) {
      this.closed.emit();
      return;
    }
    void this.router.navigateByUrl(this.returnUrl);
  }

  download(): void {
    if (this.printable) this.pdf.download(this.printable);
  }

  print(): void {
    document.body.classList.add('business-document-view-printing');
    const cleanup = () => {
      document.body.classList.remove('business-document-view-printing');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
  }

  private buildPrintable(workspace: BusinessDocumentWorkspaceModel): PdfDocumentPayload {
    const documentRecord = workspace.document;
    const snapshot = this.latestSnapshot(workspace.timeline);
    const lines = Array.isArray(snapshot['lines'])
      ? snapshot['lines'].filter((line): line is Record<string, unknown> => this.isRecord(line))
      : [];
    const currency = this.text(snapshot['currency']);
    const untaxed = this.firstNumber(snapshot, ['amount_untaxed', 'untaxed_amount', 'untaxed_total']);
    const tax = this.firstNumber(snapshot, ['amount_tax', 'tax_amount', 'tax_total']);
    const total = this.firstNumber(snapshot, ['total_amount', 'amount_total', 'total'])
      ?? this.number(documentRecord.amount_total);

    return {
      documentType: this.humanize(documentRecord.document_type_key),
      documentNumber: documentRecord.document_number,
      title: documentRecord.display_name,
      subtitle: documentRecord.reference,
      fields: [
        { label: 'Document type', value: this.humanize(documentRecord.document_type_key) },
        { label: 'Status', value: this.humanize(documentRecord.status) },
        { label: 'Stage', value: this.humanize(documentRecord.stage) },
        ...this.snapshotFields(snapshot),
      ],
      table: lines.length
        ? {
            headers: ['Description', 'Quantity', 'Unit price', 'Tax', 'Total'],
            rows: lines.map((line) => [
              this.text(line['description'] ?? line['product_name'] ?? line['name'] ?? line['title']) || 'Line',
              this.number(line['quantity']).toLocaleString(),
              this.money(line['unit_price'] ?? line['price'], currency),
              this.money(line['tax_amount'] ?? line['amount_tax'], currency),
              this.money(line['total_amount'] ?? line['amount_total'] ?? line['subtotal'], currency),
            ]),
          }
        : undefined,
      totals: [
        ...(untaxed === null ? [] : [{ label: 'Untaxed', value: this.money(untaxed, currency) }]),
        ...(tax === null ? [] : [{ label: 'Tax', value: this.money(tax, currency) }]),
        { label: 'Total', value: this.money(total, currency) },
      ],
      notes: this.text(snapshot['notes'] ?? snapshot['description'] ?? snapshot['purpose']),
      fileName: `${documentRecord.document_type_key}-${documentRecord.document_number}.pdf`,
    };
  }

  private latestSnapshot(timeline: Array<Record<string, unknown>>): Record<string, unknown> {
    const latest = timeline.length ? timeline[timeline.length - 1] : null;
    const record = latest?.['record'];
    return this.isRecord(record) ? record : {};
  }

  private snapshotFields(snapshot: Record<string, unknown>): Array<{ label: string; value: string }> {
    const candidates = [
      ['document_date', 'Document date'],
      ['expense_date', 'Expense date'],
      ['expected_date', 'Expected date'],
      ['expected_arrival', 'Expected arrival'],
      ['customer_name', 'Customer'],
      ['vendor_name', 'Vendor'],
      ['employee_name', 'Employee'],
      ['payment_reference', 'Payment reference'],
    ] as const;
    return candidates
      .filter(([key]) => this.text(snapshot[key]).length > 0)
      .slice(0, 4)
      .map(([key, label]) => ({ label, value: this.text(snapshot[key]) }));
  }

  private firstNumber(record: Record<string, unknown>, keys: string[]): number | null {
    for (const key of keys) {
      if (record[key] !== null && record[key] !== undefined && record[key] !== '') {
        return this.number(record[key]);
      }
    }
    return null;
  }

  private number(value: unknown): number {
    const parsed = Number(value ?? 0);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private money(value: unknown, currency: string): string {
    const amount = this.number(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return currency ? `${amount} ${currency}` : amount;
  }

  private text(value: unknown): string {
    return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
  }

  private humanize(value: string): string {
    return value
      .replace(/[._-]+/g, ' ')
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return !!value && typeof value === 'object' && !Array.isArray(value);
  }
}
